import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Feature, Polygon } from "geojson";
import { Restriction } from "types";
import { getRestrictionTypeName } from "@/helper/format-data";

import { TimeSpanContent } from "components/time-span/time-span-item";
import { twMerge } from "tailwind-merge";

type CurbZoneRestrictionByDecreeProps = {
  listRestrictions: Restriction[];
  onSelectDescriptionCard: (restrictionFeature: Feature<Polygon, Restriction>) => void;
  selectedDescriptionCardId: string | number | undefined;
};

export const RestrictionByDecree = ({
  listRestrictions,
  onSelectDescriptionCard,
  selectedDescriptionCardId,
}: CurbZoneRestrictionByDecreeProps) => {
  if (!listRestrictions?.length) {
    return <EmptyData />;
  }

  const listPolygonsRestrictions = listRestrictions?.map((item) => {
    return {
      properties: { ...item },
      type: "Feature",
      geometry: item.geometry,
      id: item.id,
    } as Feature<Polygon, Restriction>;
  });

  return (
    <div className="transition-opacity absolute top-16 list-disabled-curb-zones right-6 z-5 w-80 overflow-y-auto">
      {listPolygonsRestrictions.map((restriction, idx) => (
        <CurbZoneRestrictionItem
          onSelectedRestriction={() => onSelectDescriptionCard(restriction)}
          curbZoneRestriction={restriction}
          key={`disabled-detail-${idx}`}
          active={restriction.id === selectedDescriptionCardId}
        />
      ))}
    </div>
  );
};

const EmptyData = () => (
  <div className="transition-opacity absolute h-11 top-16 bg-white right-6 rounded-md shadow-lg z-5 w-80">
    <Table>
      <TableBody>
        <TableRow className="hover:none bg-white">
          <TableCell className="text-sm text-center">No results found.</TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </div>
);

type RestrictionItemProps = {
  curbZoneRestriction: Feature<Polygon, Restriction>;
  onSelectedRestriction: () => void;
  active: boolean;
};

const CurbZoneRestrictionItem = ({ curbZoneRestriction, onSelectedRestriction, active }: RestrictionItemProps) => {
  return (
    <div
      onClick={onSelectedRestriction}
      role="presentation"
      className={twMerge(
        "relative transition-opacity h-auto pt-3 pb-3 mb-2 rounded-md shadow-lg z-10 bg-white border-l-4 border-red-500 cursor-pointer",
        active && "bg-neutral-300",
      )}
    >
      <div className="flex flex-col justify-between pr-2.5 pl-5">
        <dt className="text-primary mr-2 text-tiny">Type</dt>
        {curbZoneRestriction.properties?.type && (
          <dd className="text-description-value break-all text-primary font-bold">
            {getRestrictionTypeName(curbZoneRestriction.properties?.type)}
          </dd>
        )}
      </div>
      <div className="pr-2.5 pl-5">
        <span className="text-tiny">Start Date - End Date</span>
        <div>
          {curbZoneRestriction.properties?.timeSpans?.map((timeSpan, timeSpanIndex) => (
            <div className="flex items-center border-b py-2 border-gray-900" key={`time-span-${timeSpanIndex}`}>
              <TimeSpanContent
                timeSpan={timeSpan}
                name={`timeSpans[${timeSpanIndex}]`}
                options={{
                  showSelectDate: true,
                }}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="pr-2.5 pl-5">
        <span className="text-tiny">Description</span>
        <div>
          <span className="font-bold text-sm">{curbZoneRestriction.properties?.description}</span>
        </div>
      </div>
      <div className="pr-2.5 pl-5">
        <span className="text-tiny">Places calculées</span>
        <div>
          <span className="font-bold text-sm">{curbZoneRestriction.properties?.calculatedSpaces}</span>
        </div>
      </div>
    </div>
  );
};
